import { getBlobPromptHistoryWriteUrl, getBlobTxtUrl } from '../auth/blobConfig';

/**
 * Archivo de instrucciones de un servicio. En modo dominio, `ruta` identifica
 * el blob del contenedor "prompt"; en servicios antiguos es relativa a
 * "promptsprocesos". `nombre` conserva la identidad lógica completa del archivo.
 */
export interface InstruccionServicio {
  nombre: string;
  ruta: string;
  principal: boolean;
  almacenamiento?: 'dominio';
  agente?: string;
  dominio?: string;
  contenedor?: 'prompt';
}

function ensureTextExtension(value: string): string {
  return /\.(txt|md)$/i.test(value) ? value : `${value}.txt`;
}

function fileNameFromPath(path: string): string {
  const normalized = String(path || '').trim().replace(/^\/+/, '');
  return ensureTextExtension(normalized.split('/').filter(Boolean).pop() || normalized);
}

function canonicalInstructionBlobPath(path: string): string {
  const normalized = String(path || '').trim().replace(/^\/+/, '');
  if (!normalized) return '';
  const withExt = ensureTextExtension(normalized);
  return withExt.includes('/') ? withExt : `desarrollo/${withExt}`;
}

const DEFAULT_AGENT_INSTRUCTIONS: Record<string, InstruccionServicio[]> = {
  'agtjira-catalog': [
    {
      nombre: 'AGENTS.md',
      ruta: 'desarrollo/agtjira-catalog.md',
      principal: true,
    },
    {
      nombre: 'knowledge/CATALOGO_PRINCIPAL.md',
      ruta: 'desarrollo/agtjira-catalog/knowledge/CATALOGO_PRINCIPAL.md',
      principal: false,
    },
    {
      nombre: 'knowledge/REGLAS_CHEMA.md',
      ruta: 'desarrollo/agtjira-catalog/knowledge/REGLAS_CHEMA.md',
      principal: false,
    },
  ],
  'agtjira-senor-lobo': [
    {
      nombre: 'AGENTS.md',
      ruta: 'desarrollo/agtjira-senor-lobo.md',
      principal: true,
    },
    {
      nombre: 'AGENTS.learned.md',
      ruta: 'desarrollo/agtjira-senor-lobo/AGENTS.learned.md',
      principal: false,
    },
  ],
};

function resolveAgentServiceId(configData: any, configuredPrompt: string, serviceIdFallback?: string): string {
  const explicitId = String(configData?.id || serviceIdFallback || '').trim();
  if (explicitId && DEFAULT_AGENT_INSTRUCTIONS[explicitId]) {
    return explicitId;
  }
  const promptBase = fileNameFromPath(configuredPrompt).replace(/\.(txt|md)$/i, '');
  if (promptBase && DEFAULT_AGENT_INSTRUCTIONS[promptBase]) {
    return promptBase;
  }
  return explicitId;
}

/**
 * Lee los archivos de instrucciones de la configuración del servicio.
 *
 * - Si trae `instrucciones` (no vacío), devuelve esa lista con el principal primero.
 * - Para agentes con archivos de conocimiento conocidos (como `agtjira-catalog`),
 *   asegura siempre sus archivos aunque el JSON del Blob aún no tuviera `instrucciones`.
 * - Si no, devuelve un único archivo principal a partir de `prompt` (comportamiento de siempre).
 * - Si tampoco hay `prompt`, devuelve una lista vacía.
 */
export function parseInstruccionesServicio(configData: any, serviceIdFallback?: string): InstruccionServicio[] {
  const configuredPrompt = String(configData?.prompt || '').trim();
  const rawList = Array.isArray(configData?.instrucciones) ? configData.instrucciones : [];
  const resolvedServiceId = resolveAgentServiceId(configData, configuredPrompt, serviceIdFallback);
  const domainMode = rawList.some((item: any) => item?.almacenamiento !== undefined);
  // Un descriptor de dominio es autoritativo: no añadir rutas del almacén antiguo.
  const expectedAgentId = KNOWN_AGENT_FACTORY_SPECS[serviceIdFallback || resolvedServiceId]?.agentId ||
    KNOWN_AGENT_FACTORY_SPECS[resolvedServiceId]?.agentId;
  let domainAgentId = '';
  const defaultAgentFiles = domainMode ? [] : DEFAULT_AGENT_INSTRUCTIONS[resolvedServiceId] || [];

  const parsed: InstruccionServicio[] = [];
  const seenCanonicalRutas = new Set<string>();
  for (const item of [...rawList, ...defaultAgentFiles]) {
    if (domainMode) {
      const nombre = String(item?.nombre || '').trim();
      const agente = String(item?.agente || '').trim();
      const dominio = String(item?.dominio || '').trim();
      const ruta = String(item?.ruta || '').trim();
      if (item?.almacenamiento !== 'dominio' || item?.contenedor !== 'prompt' ||
          !/^[a-z0-9-]+\.[a-z0-9-]+$/.test(agente) || dominio !== agente.split('.')[0] ||
          !nombre || nombre.split('/').some((part: string) => !part || part === '.' || part === '..') ||
          !nombre.endsWith('.md') || ruta !== `${agente}/${nombre}` ||
          (expectedAgentId && agente !== expectedAgentId) || (domainAgentId && agente !== domainAgentId)) {
        throw new Error('La configuración de instrucciones del dominio no es válida.');
      }
      domainAgentId = agente;
      if (!seenCanonicalRutas.has(ruta)) {
        seenCanonicalRutas.add(ruta);
        parsed.push({ nombre, ruta, principal: item?.principal === true,
          almacenamiento: 'dominio', agente, dominio, contenedor: 'prompt' });
      }
      continue;
    }
    const ruta = String(item?.ruta || '').trim().replace(/^\/+/, '');
    const canonicalRuta = canonicalInstructionBlobPath(ruta);
    if (!ruta || !canonicalRuta || seenCanonicalRutas.has(canonicalRuta)) continue;
    seenCanonicalRutas.add(canonicalRuta);
    const nombre = String(item?.nombre || '').trim().replace(/^\/+/, '') || fileNameFromPath(ruta);
    parsed.push({ nombre, ruta, principal: item?.principal === true });
  }

  if (!parsed.length) {
    if (!configuredPrompt) return [];
    return [{ nombre: fileNameFromPath(configuredPrompt), ruta: configuredPrompt, principal: true }];
  }

  // Exactamente un principal: el marcado; si no hay ninguno, el que coincide con
  // `prompt`; y si tampoco, el primero de la lista.
  let principalIndex = parsed.findIndex((item) => item.principal);
  if (principalIndex === -1 && configuredPrompt) {
    const canonicalPrompt = canonicalInstructionBlobPath(configuredPrompt);
    principalIndex = parsed.findIndex(
      (item) =>
        item.ruta === configuredPrompt.replace(/^\/+/, '') ||
        canonicalInstructionBlobPath(item.ruta) === canonicalPrompt
    );
  }
  if (principalIndex === -1) principalIndex = 0;

  const normalized = parsed.map((item, index) => ({ ...item, principal: index === principalIndex }));
  return [normalized[principalIndex], ...normalized.filter((_, index) => index !== principalIndex)];
}

/** Selección exacta: nunca abrir otro archivo ante un enlace antiguo o ambiguo. */
export function selectInstructionFile(files: InstruccionServicio[], requested = ''): InstruccionServicio {
  const selected = requested
    ? files.find((file) => file.nombre === requested || file.ruta === requested)
    : files[0];
  if (!selected) throw new Error(`No se encuentra el archivo de instrucciones solicitado: ${requested}.`);
  return selected;
}

/**
 * Marca UTC para el historial con formato YYYYMMDDTHHMMSSffffffZ
 * (milisegundos completados con ceros hasta microsegundos).
 */
export function buildHistoryTimestamp(date: Date = new Date()): string {
  const pad = (value: number, size = 2) => String(value).padStart(size, '0');
  return (
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}` +
    `${pad(date.getUTCMilliseconds(), 3)}000Z`
  );
}

/**
 * Antes de sobrescribir un archivo de instrucciones, copia su versión actual de la
 * cuenta principal a historial/{ID}/{nombre}/{marca}.md. Si el archivo no existe,
 * no hay nada que guardar. Si no se puede leer o escribir el historial, lanza un
 * error para no sobrescribir sin haber conservado la versión anterior.
 */
export async function archivarVersionAnterior(serviceId: string, archivo: InstruccionServicio): Promise<void> {
  const readUrl = await getBlobTxtUrl(archivo.ruta);
  const currentRes = await fetch(readUrl, { cache: 'no-store' });

  if (currentRes.status === 404) return;
  if (!currentRes.ok) {
    throw new Error(`No se pudo leer la versión actual de "${archivo.nombre}" para el historial (${currentRes.status}).`);
  }

  const previousContent = await currentRes.text();
  const historyUrl = await getBlobPromptHistoryWriteUrl(serviceId, archivo.nombre, buildHistoryTimestamp());
  const historyRes = await fetch(historyUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': 'text/markdown; charset=utf-8',
    },
    body: previousContent,
  });

  if (!historyRes.ok) {
    throw new Error(`No se pudo guardar el historial de "${archivo.nombre}" (${historyRes.status}).`);
  }
}

export interface AgentFactoryMetadata {
  serviceId: string;
  agentId: string;
  displayName: string;
  domain: string;
  archetype: string;
  description: string;
  owner: {
    requestedBy?: string;
    manager?: string;
  };
  authorizedUsers: string[];
  paramsKeys: string[];
  runtimeCapabilities: string[];
  readsInstructionsAtRuntime: boolean;
}

const ARCHETYPE_CAPABILITIES: Record<
  string,
  { readsInstructionsAtRuntime: boolean; capabilities: string[] }
> = {
  'supervisor-blob-teams': {
    readsInstructionsAtRuntime: true,
    capabilities: [
      'Lee system_prompt.md desde Blob en runtime (caché ~300 s).',
      'Supervisión periódica de contenedores Blob contra reglas markdown cuando está configurada.',
      'Motor de reporting CSV declarativo (params.reporting): consultas, filtros, agregaciones, tablas y gráficos por GPT-CEU, Teams y HTTP.',
      'Motor de búsqueda en colecciones JSON (params.recordLookup): índice y detalle JSON por registro cuando está configurado.',
      'Envío de avisos y notificaciones por Teams mediante la cuenta del dominio (teams.sender).',
      'Canales activos: GPT-CEU (webhook HTTP), Teams y endpoint HTTP.',
    ],
  },
  'process-sharepoint-teams': {
    readsInstructionsAtRuntime: true,
    capabilities: [
      'Lee system_prompt.md desde Blob en runtime (caché ~300 s).',
      'Formulario web autenticado Microsoft 365 generado desde params.form (shared/forms.py).',
      'Registro y consulta de solicitudes/expedientes y adjuntos en listas y bibliotecas de SharePoint (params.sharePoint).',
      'Flujo human-in-the-loop de revisión y aprobación por etapas/roles (params.workflow) con avisos por Teams y correo.',
      'Generación opcional de certificados Word desde plantillas de SharePoint (params.certificates).',
      'Canales activos: GPT-CEU (webhook HTTP), Teams y formulario/portal web HTTP.',
    ],
  },
  'jira-cataloger': {
    readsInstructionsAtRuntime: true,
    capabilities: [
      'Lee AGENTS.md y los documentos de knowledge/… (CATALOGO_PRINCIPAL.md, REGLAS_CHEMA.md) desde Blob en runtime (caché ~300 s).',
      'Consulta de incidencias y actividad reciente en Jira Service Management IT (params.jiraBaseUrl, params.jql).',
      'Catalogación declarativa de tickets abiertos (tipo de solicitud, componente, grupo resolutor, etiquetas de handoff) sin ejecutar resoluciones ni cerrar tickets.',
      'Aprendizaje y edición regla a regla de reglas operativas autorizadas en knowledge/REGLAS_CHEMA.md.',
      'Preparación de solicitudes de cambio técnico al Orquestador cuando hace falta desarrollo.',
      'Canales activos: GPT-CEU (webhook HTTP), Teams y worker de catalogación Jira.',
    ],
  },
  'worker-containerapp': {
    readsInstructionsAtRuntime: true,
    capabilities: [
      'Lee AGENTS.md y archivos declarados en params.instructionsFiles (AGENTS.learned.md) desde Blob en runtime (caché ~300 s).',
      'Bucle LangGraph (plan → ejecutar → verificar) sobre incidencias Jira de la JQL de traspaso (params.handoffLabels).',
      'Registro de comentarios internos/públicos con trazabilidad en Jira y gestión de etiquetas (unresolvedLabel, pausedResolutionLabel, pendingConfirmationLabel).',
      'Ejecución de resoluciones deterministas y asistidas configuradas, transicionando a estado finalizado solo cuando la resolución es segura.',
      'Bloque gestionado por máquina <!-- reglas-dinamicas:inicio --> … ```solucionador-rules-v1``` … <!-- reglas-dinamicas:fin -->.',
      'Canales activos: GPT-CEU (webhook HTTP), Teams y Container App sobre Jira.',
    ],
  },
  'domain-router': {
    readsInstructionsAtRuntime: false,
    capabilities: [
      'AVISO DE RUNTIME: Arquetipo domain-router. No lee instrucciones en runtime para decidir el enrutado (las reglas viven en params.routing y params.subagentes de agent.yaml); editar su archivo solo documenta el agente.',
      'Puerta de entrada de dominio que reparte cada consulta entre los subagentes a los que el usuario tiene acceso, reenviando su token.',
      'Canales activos: GPT-CEU (webhook HTTP) y endpoint HTTP.',
    ],
  },
  'browser-local-teams': {
    readsInstructionsAtRuntime: false,
    capabilities: [
      'AVISO DE RUNTIME: Arquetipo browser-local-teams. No lee instrucciones en runtime; los hosts, operaciones y selectores viven en params.browser de agent.yaml. Editar este archivo solo documenta el agente y no cambia su comportamiento.',
      'Broker en Azure/Teams y runner local sobre un perfil dedicado de Chrome en modo acotado.',
      'Canales activos: GPT-CEU (webhook HTTP), Teams y runner local Chrome.',
    ],
  },
  'academic-services-api': {
    readsInstructionsAtRuntime: false,
    capabilities: [
      'AVISO DE RUNTIME: Arquetipo academic-services-api. No lee instrucciones en runtime; las operaciones SOAP/ASMX, vistas SQL Server, autorizaciones por rol y documentos están gobernadas por params en agent.yaml. Editar este archivo solo documenta el agente y no cambia su comportamiento.',
      'Consultas académicas autenticadas en modo lectura (SOAP/ASMX + copia SQL Server declarada), generación de listados/certificados XLSX/DOCX/PDF y notificaciones por correo tras cierre confirmado.',
      'Canales activos: GPT-CEU (webhook HTTP) y endpoints HTTP (query, export, certificate, health).',
    ],
  },
};

const KNOWN_AGENT_FACTORY_SPECS: Record<
  string,
  Omit<AgentFactoryMetadata, 'serviceId' | 'runtimeCapabilities' | 'readsInstructionsAtRuntime'>
> = {
  'agtservicios-academicos': {
    agentId: 'alexia.servicios-academicos',
    displayName: 'Servicios Académicos Alexia ISEP CV',
    domain: 'alexia',
    archetype: 'academic-services-api',
    description:
      'Agente de servicios académicos de ISEP CV que consulta Alexia en modo lectura para datos personales, matrículas, bajas, grupos, expedientes, módulos, repetidores y notas; genera listados y certificados XLSX, DOCX o PDF y ejecuta notificaciones de calificaciones por correo.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'jroig@uchceu.es' },
    authorizedUsers: ['jroig@uchceu.es'],
    paramsKeys: ['httpRoute', 'exportRoute', 'certificateRoute', 'healthRoute', 'academicProvider', 'authorization', 'operations', 'database', 'documents', 'notifications', 'integrations', 'requiredEnv'],
  },
  'agtagente-supervisor': {
    agentId: 'crm.agente-supervisor',
    displayName: 'Agente Supervisor de Conversaciones CEU',
    domain: 'crm',
    archetype: 'supervisor-blob-teams',
    description:
      'Supervisa las conversaciones de WhatsApp (SI/SA) de los asistentes de admisión de CEU almacenadas en Blob Storage contra un documento markdown de reglas y avisa por Teams al supervisor y a los responsables de cada centro.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'jaime.orfilagarcia@ceu.es' },
    authorizedUsers: ['jaime.orfilagarcia@ceu.es'],
    paramsKeys: ['foundry', 'storageContainer', 'rulesBlob', 'messageMarker', 'settingsMigration', 'rulesTeams', 'supervisedContainers', 'containerMetadata', 'notificationFields', 'activitySummary', 'reviewLinks'],
  },
  'agtimpagados': {
    agentId: 'impagados.impagados',
    displayName: 'Impagados',
    domain: 'impagados',
    archetype: 'domain-router',
    description:
      'Puerta de entrada del dominio Impagados: deriva consultas a impagados_uch o impagados_fp_vlc según el centro indicado y los permisos del usuario.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'lorena@uchceu.es' },
    authorizedUsers: ['lorena@uchceu.es', 'jroig@uchceu.es'],
    paramsKeys: ['httpRoute', 'healthRoute', 'routing', 'integrations', 'requiredEnv', 'subagentes'],
  },
  'agtimpagados-fp-vlc': {
    agentId: 'impagados.impagados-fp-vlc',
    displayName: 'impagados_fp_vlc',
    domain: 'impagados',
    archetype: 'supervisor-blob-teams',
    description:
      'Agente de consulta de impagados de FP Valencia preparado para su futura fuente de datos. Incluye preparación de avisos WhatsApp con envío bloqueado hasta autorización explícita.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'lorena@uchceu.es' },
    authorizedUsers: ['lorena@uchceu.es', 'jroig@uchceu.es'],
    paramsKeys: ['storageContainer', 'supervisedContainers', 'httpRoute', 'reportingDefaultDomain', 'reporting', 'integrations', 'requiredEnv', 'notificationCampaign'],
  },
  'agtimpagados-uch': {
    agentId: 'impagados.impagados-uch',
    displayName: 'impagados_uch',
    domain: 'impagados',
    archetype: 'supervisor-blob-teams',
    description:
      'Consulta importes pendientes y alumnado con impago de la UCH, con desglose por campus, centro, plan de estudios y actividad, desde vista_impagados.csv. Incluye preparación de avisos WhatsApp con envío bloqueado hasta autorización explícita.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'lorena@uchceu.es' },
    authorizedUsers: ['lorena@uchceu.es', 'jroig@uchceu.es'],
    paramsKeys: ['storageContainer', 'supervisedContainers', 'httpRoute', 'reportingDefaultDomain', 'reporting', 'integrations', 'requiredEnv', 'reportingStorageContainer', 'notificationCampaign'],
  },
  'agtjira-catalog': {
    agentId: 'it.jira-catalog',
    displayName: 'Agente de Catalogación Jira IT',
    domain: 'it',
    archetype: 'jira-cataloger',
    description:
      'Conversa sobre tickets y el servicio Jira IT: explica catalogaciones, consulta incidencias y resume resultados recientes con datos comprobados. Aprende y aplica instrucciones de catalogación autorizadas en lenguaje natural, y deja los tickets abiertos listos para el equipo resolutor; no resuelve ni cierra tickets.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'jmhuerta@uchceu.es' },
    authorizedUsers: ['jmhuerta@uchceu.es', 'jroig@uchceu.es'],
    paramsKeys: ['changePolicy', 'foundry', 'jiraBaseUrl', 'jql', 'teamsNotifyRecipients', 'integrations', 'requiredEnv', 'catalog'],
  },
  'agtjira-senor-lobo': {
    agentId: 'it.jira-senor-lobo',
    displayName: 'Solucionador IT',
    domain: 'it',
    archetype: 'worker-containerapp',
    description:
      'Resuelve incidencias de Jira Service Management IT que el catalogador u otros flujos marquen como auto-resolubles. Vigila una JQL de traspaso por labels, analiza cada ticket con un grafo LangGraph, deja trazabilidad en Jira y, solo cuando la resolución es segura, transiciona el ticket a un estado finalizado.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'jmhuerta@uchceu.es' },
    authorizedUsers: ['jmhuerta@uchceu.es', 'jroig@uchceu.es'],
    paramsKeys: ['changePolicy', 'foundry', 'jiraBaseUrl', 'jql', 'handoffLabels', 'unresolvedLabel', 'pausedResolutionLabel', 'pendingConfirmationLabel', 'handoffContract', 'internalComments', 'commentDisplayName', 'integrations', 'requiredEnv', 'deterministicResolutions', 'teamsNotifyRecipients', 'instructionsFiles', 'selfConfiguration'],
  },
  'agtdatos-admision': {
    agentId: 'kpi-uch.datos-admision',
    displayName: 'Agente de consulta de datos de la UCH',
    domain: 'kpi-uch',
    archetype: 'supervisor-blob-teams',
    description:
      'Supervisa CSVs de KPI-UCH almacenados en Blob Storage y responde por Teams y GPT-CEU a consultas de reporting de admisiones, impagados, matrículas y tasas de asignaturas.',
    owner: { requestedBy: 'lorena@uchceu.es', manager: 'lorena@uchceu.es' },
    authorizedUsers: ['lorena@uchceu.es', 'jroig@uchceu.es', 'ruben.garciagarcia@uchceu.es', 'diego.curbeiragaray@uchceu.es'],
    paramsKeys: ['maintenance', 'foundry', 'storageContainer', 'supervisedContainers', 'tasasAsignaturas', 'tasasTitulaciones', 'feedback', 'reportingDefaultDomain', 'reportingDomainRoute', 'externalQueries', 'httpAuth', 'reporting', 'reportingDomains', 'rooms', 'domainRouting', 'reportingCrossDomainQueries'],
  },
  'agtdirectorio': {
    agentId: 'personas.directorio',
    displayName: 'Directorio de Personas CEU',
    domain: 'personas',
    archetype: 'supervisor-blob-teams',
    description:
      'Consulta el directorio corporativo de personas del CEU: localiza empleados y profesionales por nombre, cargo, puesto, departamento, centro, sede, ubicación o edificio, y responde quién trabaja dónde.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'jroig@uchceu.es' },
    authorizedUsers: ['jroig@uchceu.es', 'ana.leivabrondo@uchceu.es'],
    paramsKeys: ['foundry', 'storageContainer', 'supervisedContainers', 'httpRoute', 'httpAuth', 'recordLookup', 'integrations', 'requiredEnv'],
  },
  'agthorarios-profesores-uch': {
    agentId: 'personas.horarios-profesores-uch',
    displayName: 'Horarios de profesores UCH',
    domain: 'personas',
    archetype: 'supervisor-blob-teams',
    description:
      'Consulta los horarios lectivos del profesorado de la UCH por GPT-CEU, por Teams y mediante un endpoint HTTP: día, hora, asignatura, grupo, aula, edificio y campus de un profesor concreto o de quienes coinciden por nombre, código, correo o campus.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'jroig@uchceu.es' },
    authorizedUsers: ['jroig@uchceu.es', 'ana.leivabrondo@uchceu.es'],
    paramsKeys: ['foundry', 'storageContainer', 'supervisedContainers', 'httpRoute', 'httpAuth', 'recordLookup', 'integrations', 'requiredEnv'],
  },
  'agtasignacion-tutores': {
    agentId: 'sigma.asignacion-tutores',
    displayName: 'SIGMA Asignación de Tutores',
    domain: 'sigma',
    archetype: 'browser-local-teams',
    description:
      'Agente local para validar y previsualizar lotes Excel segmentados por titulación, sin ejecutar asignaciones ni actualizar datos en SIGMA.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'jroig@uchceu.es' },
    authorizedUsers: ['jroig@uchceu.es'],
    paramsKeys: ['httpRoute', 'foundry', 'teamsRouting', 'broker', 'browser', 'governance', 'integrations', 'requiredEnv'],
  },
  'agtbecas-mec-consultas': {
    agentId: 'sigma.becas-mec-consultas',
    displayName: 'SIGMA Consultas de Becas MEC',
    domain: 'sigma',
    archetype: 'browser-local-teams',
    description:
      'Agente de solo lectura para comprobar por DNI o pasaporte la existencia y el estado visible de solicitudes de becas MEC ya incorporadas a SIGMA, sin importar ficheros, cargar resoluciones ni modificar solicitudes.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'jroig@uchceu.es' },
    authorizedUsers: ['jroig@uchceu.es'],
    paramsKeys: ['httpRoute', 'foundry', 'teamsRouting', 'broker', 'browser', 'governance', 'integrations', 'requiredEnv'],
  },
  'agtexpedientes': {
    agentId: 'sigma.expedientes',
    displayName: 'SIGMA Expedientes',
    domain: 'sigma',
    archetype: 'browser-local-teams',
    description:
      'Agente de consulta que recibe una pregunta, un DNI o NIE y el tipo de estudio por un chat 1:1 autorizado, abre SIGMA en el perfil local y dedicado de Chrome y devuelve el dato visible solicitado sin modificar información académica.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'jroig@uchceu.es' },
    authorizedUsers: ['jroig@uchceu.es'],
    paramsKeys: ['httpRoute', 'foundry', 'teamsRouting', 'broker', 'browser', 'governance', 'integrations', 'requiredEnv'],
  },
  'agtexplodat-catalogo': {
    agentId: 'sigma.explodat-catalogo',
    displayName: 'SIGMA Catálogo EXPLODAT',
    domain: 'sigma',
    archetype: 'browser-local-teams',
    description:
      'Agente de solo lectura para buscar y describir plantillas y extractores disponibles en EXPLODAT, sin ejecutar extractores, generar descargas, grabar, modificar ni eliminar plantillas.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'jroig@uchceu.es' },
    authorizedUsers: ['jroig@uchceu.es'],
    paramsKeys: ['httpRoute', 'foundry', 'teamsRouting', 'broker', 'browser', 'governance', 'integrations', 'requiredEnv'],
  },
  'agtfnr-consultas': {
    agentId: 'sigma.fnr-consultas',
    displayName: 'SIGMA Consultas FNR',
    domain: 'sigma',
    archetype: 'browser-local-teams',
    description:
      'Agente de solo lectura para preparar y supervisar matrículas de Formación No Reglada mediante consultas acotadas de matrícula, documentación y planes de estudio, sin confirmar, modificar ni crear datos en SIGMA.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'jroig@uchceu.es' },
    authorizedUsers: ['jroig@uchceu.es', 'leya@uchceu.es'],
    paramsKeys: ['httpRoute', 'foundry', 'teamsRouting', 'broker', 'browser', 'governance', 'integrations', 'requiredEnv'],
  },
  'agttfg-practicas-seguimiento': {
    agentId: 'sigma.tfg-practicas-seguimiento',
    displayName: 'SIGMA Seguimiento TFG y Prácticas',
    domain: 'sigma',
    archetype: 'browser-local-teams',
    description:
      'Agente de solo lectura para consultar por NIP las solicitudes y el seguimiento visible de TFG, TFM y prácticas de empresa, sin crear anexos, certificados, ofertas, asignaciones ni cambios académicos.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'jroig@uchceu.es' },
    authorizedUsers: ['jroig@uchceu.es'],
    paramsKeys: ['httpRoute', 'foundry', 'teamsRouting', 'broker', 'browser', 'governance', 'integrations', 'requiredEnv'],
  },
  'agttitulos-consultas': {
    agentId: 'sigma.titulos-consultas',
    displayName: 'SIGMA Consulta de Títulos',
    domain: 'sigma',
    archetype: 'browser-local-teams',
    description:
      'Agente de solo lectura para localizar por DNI o pasaporte solicitudes de título y devolver su estado visible, sin validar, enviar, notificar, entregar ni modificar solicitudes o títulos.',
    owner: { requestedBy: 'jroig@uchceu.es', manager: 'jroig@uchceu.es' },
    authorizedUsers: ['jroig@uchceu.es'],
    paramsKeys: ['httpRoute', 'foundry', 'teamsRouting', 'broker', 'browser', 'governance', 'integrations', 'requiredEnv'],
  },
  'agtbeca-impulso-gva': {
    agentId: 'utd-uch.beca-impulso-gva',
    displayName: 'BECA IMPULSO GVA',
    domain: 'utd-uch',
    archetype: 'process-sharepoint-teams',
    description:
      'Permite al alumnado de la UCH presentar solicitudes de la Beca Impulso GVA, indicando su campus y titulación y aportando la documentación requerida, registra en SharePoint, envía correo de confirmación y ofrece portal de lectura a usuarios autorizados.',
    owner: { requestedBy: 'miquel.munozromero@uchceu.es', manager: 'jroig@uchceu.es' },
    authorizedUsers: ['usayd1@uchceu.es', 'mfcastiella@uchceu.es'],
    paramsKeys: ['foundry', 'httpRoute', 'specRoute', 'process', 'form', 'portal', 'sharePoint', 'workflow', 'automation', 'governance', 'integrations', 'requiredEnv'],
  },
  'agtpromocion-profesorado-2026': {
    agentId: 'utd-uch.promocion-profesorado-2026',
    displayName: 'UCH - Promoción Profesorado 2026',
    domain: 'utd-uch',
    archetype: 'process-sharepoint-teams',
    description:
      'Proceso UTD con formulario autenticado por Microsoft 365 para recibir solicitudes de promoción del profesorado 2026, registrar datos y documentación en SharePoint y coordinar revisiones humanas por etapas.',
    owner: { requestedBy: 'jose.juliajarillo@uchceu.es', manager: 'usayd1@uchceu.es' },
    authorizedUsers: ['jose.juliajarillo@uchceu.es', 'usayd@uchceu.es'],
    paramsKeys: ['foundry', 'httpRoute', 'specRoute', 'process', 'form', 'sharePoint', 'workflow', 'automation', 'governance', 'integrations', 'requiredEnv', 'caseManagement'],
  },
  'agtpruebas-formulario-sharepoint': {
    agentId: 'utd-uch.pruebas-formulario-sharepoint',
    displayName: 'Pruebas UTD · formulario y SharePoint',
    domain: 'utd-uch',
    archetype: 'process-sharepoint-teams',
    description:
      'Servicio interno de pruebas del equipo UTD para registrar envíos de prueba con datos ficticios y consultar cómo va una prueba ya enviada.',
    owner: { requestedBy: 'diego.curbeiragaray@uchceu.es', manager: 'jroig@uchceu.es' },
    authorizedUsers: ['diego.curbeiragaray@uchceu.es'],
    paramsKeys: ['foundry', 'httpRoute', 'specRoute', 'process', 'form', 'sharePoint', 'portal', 'notifications', 'workflow', 'automation', 'humanApprovalRequiredFor', 'governance', 'integrations', 'requiredEnv'],
  },
  'agtsolicitud-fraccionamiento-pago-usp': {
    agentId: 'utd-uch.solicitud-fraccionamiento-pago-usp',
    displayName: 'Solicitud de Fraccionamiento de Pago',
    domain: 'utd-uch',
    archetype: 'process-sharepoint-teams',
    description:
      'Permite presentar solicitudes de fraccionamiento de pago y consultar el seguimiento de una solicitud ya enviada.',
    owner: { requestedBy: 'miquel.munozromero@uchceu.es', manager: 'jroig@uchceu.es' },
    authorizedUsers: ['usayd1@uchceu.es'],
    paramsKeys: ['foundry', 'httpRoute', 'specRoute', 'process', 'form', 'sharePoint', 'notifications', 'workflow', 'automation', 'governance', 'integrations', 'requiredEnv'],
  },
};

export function isAgentFactoryService(
  serviceId?: string,
  promptFileName?: string,
  instructionFiles?: InstruccionServicio[],
  configData?: any
): boolean {
  const cleanPrompt = String(promptFileName || configData?.prompt || '').trim();
  if (/\.txt$/i.test(cleanPrompt)) {
    return false;
  }
  if (configData && configData.agent === false) {
    return false;
  }
  const cleanId = String(serviceId || '').trim();
  if (/^agt[a-z0-9_-]+$/i.test(cleanId)) {
    return true;
  }
  if (cleanId && KNOWN_AGENT_FACTORY_SPECS[cleanId]) {
    return true;
  }
  if (/^(desarrollo\/)?agt[a-z0-9_-]+(\/|\.)/i.test(cleanPrompt)) {
    return true;
  }
  if (
    Array.isArray(instructionFiles) &&
    instructionFiles.some((file) => /^(desarrollo\/)?agt[a-z0-9_-]+(\/|\.)/i.test(String(file?.ruta || '').trim()))
  ) {
    return true;
  }
  return false;
}

export function resolveAgentFactoryMetadata(
  serviceId: string,
  configData?: any,
  serviceDisplayName?: string
): AgentFactoryMetadata {
  const cleanId = String(serviceId || configData?.id || '').trim();
  const known = KNOWN_AGENT_FACTORY_SPECS[cleanId];
  const domainFile = Array.isArray(configData?.instrucciones)
    ? configData.instrucciones.find((file: any) => file?.almacenamiento === 'dominio')
    : null;
  const slug = cleanId.replace(/^agt/i, '') || 'agente';
  const principalFile = Array.isArray(configData?.instrucciones)
    ? configData.instrucciones.find((item: any) => item?.principal)?.nombre || configData.instrucciones[0]?.nombre
    : '';
  const inferredArchetype =
    known?.archetype ||
    configData?.archetype ||
    configData?.template ||
    (principalFile === 'AGENTS.md' ? 'worker-containerapp' : 'supervisor-blob-teams');
  const archetypeInfo = ARCHETYPE_CAPABILITIES[inferredArchetype] || ARCHETYPE_CAPABILITIES['supervisor-blob-teams'];

  return {
    serviceId: cleanId || `agt${slug}`,
    agentId: domainFile?.agente || known?.agentId || configData?.agentId || `agent-factory.${slug}`,
    displayName:
      known?.displayName ||
      serviceDisplayName ||
      String(configData?.NombreProceso || configData?.displayName || cleanId || slug),
    domain: domainFile?.dominio || known?.domain || configData?.domain || (known?.agentId ? known.agentId.split('.')[0] : 'general'),
    archetype: inferredArchetype,
    description:
      known?.description ||
      String(configData?.Descripcion || configData?.description || '').trim() ||
      'Agente gestionado en agent-factory.',
    owner: known?.owner || configData?.owner || {},
    authorizedUsers:
      known?.authorizedUsers ||
      (Array.isArray(configData?.authorizedUsers) ? configData.authorizedUsers : []),
    paramsKeys: known?.paramsKeys || [],
    runtimeCapabilities: archetypeInfo.capabilities,
    readsInstructionsAtRuntime: archetypeInfo.readsInstructionsAtRuntime,
  };
}

